import React, {useRef, useState} from 'react';
import {observer} from 'mobx-react';
import s from '../s';
import {modal, Portal, webp} from '../../util';
import {linoOptionLabel} from '../print-options';
import Lino from '../components/lino/lino';
import Category from '../components/doc-sizes-and-categories/category';
import {getCategoryOptions} from '../components/doc-sizes-and-categories/category-utils';
import {InsigniaPosition} from '../components/insignia/components/insignia-position';
import {getTitle} from '../components/insignia/insignia';
import RecycledIcon from '../components/moulding/components/recycled-icon';
import {GLASS_BENEFITS, GLASS_TAG, GLASS_TAG_ID} from '../components/ab-glass-options';
import {linoArtworkSrc, linoNeedsControlPicker, linoSectionShown, removeLino, selectLino} from '../selection/lino';
import {insigniaPositionShown, insigniaSectionShown, removeInsignia, selectInsignia, visibleInsignias} from '../selection/insignia';
import {isFastShipMoulding, isTrendingMoulding, mouldingOrder, selectMoulding} from '../selection/moulding';
import {isFastShipMat, matSectionShown, matTiles, selectMat} from '../selection/mat';
import {isAcrylicGlazing, isOfferedGlassTile, selectGlass} from '../selection/glass';
import {changeMonth, groupByKey, isAnsweredYear, partMonths, partName, partSeasons, windowAt, windowOf, windowYears, yearMoment} from './size-questions';
import {redesign} from './redesign-store';
import {gradYearInSchoolStep, stepsIntro, type Step, type StepPlan} from './steps-plan';
import {inches} from './copy';
import {AlertIcon, CheckIcon, TruckIcon} from './icons';
import AtcRow from './atc-row';
import {documentWord} from './overview';
import {fastShipItem} from './fast-ship';
import {isRedesignPhone} from './viewport';
import {screenResizeStore} from '../../global/store/screen-resize-store';

/** Tiles shown before "See all": the control's own counts (6 mouldings, 6 mats, 4 seals). */
const FRAMES_SHOWN = 6;
const MATS_SHOWN = 6;
const SEALS_SHOWN = 4;

const FastShipPill: React.FC = () => (
    <span className="pp3-pill pp3-pill-fast">
        <TruckIcon size={12} />
        Fast Ship
    </span>
);

type StepFrameProps = {
    step: Step;
    title: string;
    /** The pick, shown with a check next to the title. */
    picked?: React.ReactNode;
    /** "Needed for your size" in place of the pick. */
    needed?: boolean;
    /** Add To Cart was pressed with this question open: number and tag turn red. */
    missed?: boolean;
    help?: string;
    children?: React.ReactNode;
};

/** One numbered step: the number on the rail, the title with the pick or "Needed for your size", then the options. */
const StepFrame: React.FC<StepFrameProps> = ({step, title, picked, needed, missed, help, children}) => (
    <div
        id={`pp3-step-${step.id}`}
        className={`pp3-step${missed ? ' missed' : ''}`}
        role="group"
        aria-labelledby={`pp3-step-${step.id}-title`}
    >
        <span
            className="pp3-num"
            aria-hidden="true"
        >
            {step.number}
        </span>
        <div className="pp3-step-body">
            <div className="pp3-step-head">
                <h3 id={`pp3-step-${step.id}-title`}>
                    <span className="sr-only">Step {step.number}: </span>
                    {title}
                </h3>
                {needed ? (
                    <p className="pp3-needed">Needed for your size</p>
                ) : picked ? (
                    <p className="pp3-picked">
                        <CheckIcon />
                        <span>{picked}</span>
                    </p>
                ) : null}
            </div>
            {help && <p className="pp3-help">{help}</p>}
            {children}
        </div>
    </div>
);

/** Light text on a dark mat, dark text on a light one (the "Embossed on your mat" label sits on the mat colour). */
const labelOnMat = (hex: string): string => {
    const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return '#1C2430';
    const n = parseInt(m[1], 16);
    const lin = (c: number) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
    return luminance < 0.4 ? '#FFFFFF' : '#1C2430';
};

/** "Embossed on your mat": the lino's artwork on the mat colour, as the control's preview draws it; the school's name if the artwork is missing. */
const EmbossPreview: React.FC<{title: string}> = observer(({title}) => {
    const [failed, setFailed] = useState<number | null>(null);
    const color = `#${s.ps.mat1_color || 'FFFFFF'}`;
    return (
        <div
            className="pp3-emboss"
            style={{backgroundColor: color, color: labelOnMat(color)}}
        >
            <span className="pp3-emboss-label">Embossed on your mat</span>
            {failed === s.ps.lino_id ? (
                <span className="pp3-emboss-text">{title}</span>
            ) : (
                <img
                    src={linoArtworkSrc(Portal?.CDNDomain)}
                    alt={title}
                    onError={() => setFailed(s.ps.lino_id)}
                />
            )}
        </div>
    );
});

/** "This year (2026)" beside a year field that holds no year yet: the current year in one tap. */
const ThisYear: React.FC<{field: React.RefObject<HTMLInputElement>; onPick: (year: string) => void}> = ({field, onPick}) => {
    const year = String(new Date().getFullYear());
    return (
        <button
            type="button"
            className="pp3-link pp3-this-year"
            onClick={e => {
                onPick(year);
                // the link goes once the field holds a year: a key press (detail 0) moves focus to the field; a tap does not, so no phone keyboard opens
                if (e.detail === 0) field.current?.focus();
            }}
        >
            This year ({year})
        </button>
    );
};

/** The graduation year in the school step: optional ("if available"), or required on "pre-" products. */
const SchoolYear: React.FC = observer(() => {
    const field = useRef<HTMLInputElement>(null);
    const required = !!s.prod.YearOfGraduation_Required;
    const missed = required && redesign.gradYearMissed;
    const value = s.ui.grad_year_input || '';
    return (
        <div className={`pp3-year-field${missed ? ' missed' : ''}`}>
            <label htmlFor="pp3-grad-year">
                Graduation year <span className="pp3-muted">{required ? '(required)' : '(if available)'}</span>
            </label>
            <div className="pp3-year-input">
                <input
                    ref={field}
                    id="pp3-grad-year"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    placeholder="e.g. 2026"
                    autoComplete="off"
                    value={value}
                    aria-invalid={missed || undefined}
                    aria-describedby={missed ? 'pp3-grad-year-error' : undefined}
                    onChange={e => redesign.answerYear(e.target.value, false)}
                />
                {!isAnsweredYear(value) && (
                    <ThisYear
                        field={field}
                        onPick={year => redesign.answerYear(year, false)}
                    />
                )}
            </div>
            {missed && (
                <p
                    id="pp3-grad-year-error"
                    className="pp3-error"
                >
                    <AlertIcon />
                    Enter your graduation year.
                </p>
            )}
        </div>
    );
});

/** The school (lino) picker: one select of the choices, or the control's Lino where its extra selects are needed. */
const SchoolPicker: React.FC = observer(() => {
    if (linoNeedsControlPicker()) {
        return (
            <div className="pp3-control-picker">
                <Lino />
            </div>
        );
    }

    const choices = s.linoChoices.options.filter(m => !(m.fpc_id > 0));
    const optional = s.prod.LinoID < 1;
    const value = s.ps.lino_id > 0 ? String(s.preferredLinoId(s.ps.lino_id)) : '0';
    const current = choices.find(m => String(m.id) === value);

    return (
        <div className="pp3-school-row">
            <div className="pp3-select">
                <label
                    htmlFor="pp3-school"
                    className="sr-only"
                >
                    School on your diploma
                </label>
                <select
                    id="pp3-school"
                    value={value}
                    onChange={e => {
                        const id = parseInt(e.target.value, 10);
                        if (id > 0) {
                            selectLino(id);
                        } else {
                            removeLino();
                        }
                        redesign.afterSchoolChange();
                    }}
                >
                    {(optional || !current) && <option value="0">{optional ? 'No school name' : 'Choose your school'}</option>}
                    {choices.map(m => (
                        <option
                            key={m.id}
                            value={String(m.id)}
                        >
                            {linoOptionLabel(m, m._title ?? m.title)}
                        </option>
                    ))}
                </select>
            </div>
            {s.ps.lino_id > 0 && s.prod.ProductID !== 88937 && <EmbossPreview title={current ? current.title : s.ps.lino_text || ''} />}
        </div>
    );
});

/** The picked school's name for the step header. */
const schoolPick = (): string => {
    if (!(s.ps.lino_id > 0)) return '';
    const id = s.preferredLinoId(s.ps.lino_id);
    const m = s.linoChoices.options.find(x => x.id === id) ?? s.prod.linos.find(x => x.id === s.ps.lino_id);
    return m ? (m._title ?? m.title) : s.ps.lino_text || '';
};

const SchoolStep: React.FC<{step: Step; plan: StepPlan}> = observer(({step, plan}) => {
    const lino = linoSectionShown();
    const year = gradYearInSchoolStep();
    const title = plan.sizeQuestions && lino ? 'School on your diploma' : 'Tell us about your diploma';
    const picked = lino ? schoolPick() : year && s.ui.grad_year_input && s.ui.grad_year_input.length === 4 ? s.ui.grad_year_input : '';
    return (
        <StepFrame
            step={step}
            title={title}
            picked={picked}
            missed={redesign.gradYearMissed && !!s.prod.YearOfGraduation_Required}
            help={!plan.sizeQuestions && lino ? 'Pick the school on your diploma. Its name is embossed on the mat.' : undefined}
        >
            {lino && <SchoolPicker />}
            {year && <SchoolYear />}
        </StepFrame>
    );
});

const DegreeStep: React.FC<{step: Step}> = observer(({step}) => {
    const q = redesign.questions;
    const answered = groupByKey(q, redesign.degree);
    const missed = redesign.tried && !answered;
    return (
        <StepFrame
            step={step}
            title="Degree you're framing"
            picked={answered?.label}
            needed={!answered}
            missed={missed}
        >
            <fieldset className="pp3-choices">
                <legend className="sr-only">Degree you're framing</legend>
                {q.groups.map(g => (
                    <button
                        key={g.key}
                        type="button"
                        className={`pp3-choice${redesign.degree === g.key ? ' selected' : ''}`}
                        aria-pressed={redesign.degree === g.key}
                        onClick={() => redesign.answerDegree(g.key)}
                    >
                        {g.label}
                    </button>
                ))}
            </fieldset>
            {missed && (
                <p className="pp3-error">
                    <AlertIcon />
                    Pick the degree you're framing so the frame fits.
                </p>
            )}
        </StepFrame>
    );
});

/**
 * "When did you graduate?": the year field, then, where the size changed during that year, which part of it ("Spring" January
 * to May, "Summer or fall" June to December). The year is answered once both are.
 */
const YearStep: React.FC<{step: Step; degreeStep?: Step}> = observer(({step, degreeStep}) => {
    const field = useRef<HTMLInputElement>(null);
    const q = redesign.questions;
    const typed = redesign.yearAnswered;
    const degreeOpen = q.degreeNeeded && !groupByKey(q, redesign.degree);
    const parts = redesign.parts;
    const part = redesign.pickedPart;
    const answered = typed && (parts.length === 0 || !!part);
    const missed = redesign.tried && !answered;
    const fieldMissed = missed && !typed;
    const row = s.docsizes_available.find(d => d.Id === s.docsizes_selected_id);
    const group = groupByKey(q, redesign.degree) ?? (row ? q.groups.find(g => g.windows.some(w => w.row.Id === row.Id)) : undefined);
    const window = answered && group ? windowAt(group.windows, yearMoment(Number(redesign.year), part)) : row ? windowOf(row) : undefined;
    const month = changeMonth(parts);
    const sizeSet = answered && !degreeOpen && (
        <p
            className="pp3-size-set"
            aria-live="polite"
        >
            <CheckIcon />
            <span>
                <strong>Size set: {inches(s.ps.docWidth, s.ps.docHeight)}</strong>
                {group && window ? <span className="pp3-muted"> ({`${group.label} ${documentWord()}, ${windowYears(window)}`})</span> : null}
            </span>
        </p>
    );
    return (
        <StepFrame
            step={step}
            title="When did you graduate?"
            picked={answered ? (part ? `${partName(part)} ${redesign.year}` : redesign.year) : undefined}
            needed={!answered}
            missed={missed}
            help="Enter the year on your diploma. The frame size updates to match."
        >
            <div className="pp3-year-row">
                <label
                    htmlFor="pp3-size-year"
                    className="sr-only"
                >
                    Graduation year
                </label>
                <input
                    ref={field}
                    id="pp3-size-year"
                    className={fieldMissed ? 'missed' : undefined}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    placeholder="e.g. 2026"
                    autoComplete="off"
                    value={redesign.year}
                    aria-invalid={fieldMissed || undefined}
                    aria-describedby={fieldMissed ? 'pp3-size-year-error' : undefined}
                    onChange={e => redesign.answerYear(e.target.value, true)}
                />
                {!typed && (
                    <ThisYear
                        field={field}
                        onPick={year => redesign.answerYear(year, true)}
                    />
                )}
                {parts.length === 0 && sizeSet}
                {typed && degreeOpen && (
                    <p
                        className="pp3-muted"
                        aria-live="polite"
                    >
                        Got it. Pick your degree{degreeStep ? ` in step ${degreeStep.number}` : ''} to set the size.
                    </p>
                )}
            </div>
            {parts.length > 0 && (
                <div className="pp3-part">
                    <p
                        id="pp3-part-prompt"
                        className="pp3-part-prompt"
                        aria-live="polite"
                    >
                        The {documentWord()} size changed {month ? `in ${month} ${redesign.year}` : `during ${redesign.year}`}. When in {redesign.year} did you graduate?
                    </p>
                    <div
                        className="pp3-choices"
                        role="group"
                        aria-labelledby="pp3-part-prompt"
                        aria-describedby={missed ? 'pp3-size-year-error' : undefined}
                    >
                        {parts.map((p, i) => {
                            const selected = part?.from === p.from;
                            return (
                                <button
                                    key={p.from}
                                    type="button"
                                    className={`pp3-choice pp3-part-choice${selected ? ' selected' : ''}`}
                                    aria-pressed={selected}
                                    data-pp3-focus={i === 0 && !part ? '' : undefined}
                                    onClick={() => redesign.answerPart(p)}
                                >
                                    <span>{partName(p)}</span>
                                    {partSeasons(p) && <span className="pp3-part-months">{partMonths(p)}</span>}
                                </button>
                            );
                        })}
                    </div>
                    {sizeSet}
                </div>
            )}
            {missed && (
                <p
                    id="pp3-size-year-error"
                    className="pp3-error"
                >
                    <AlertIcon />
                    {typed ? `Pick when in ${redesign.year} you graduated so the frame fits.` : redesign.year.length > 0 ? 'Check the year. Enter all 4 digits, like 2024.' : 'Enter your graduation year so the frame fits.'}
                </p>
            )}
        </StepFrame>
    );
});

/** A control category ("Degree Type", "Size / Orientation"...), kept whole: its own options, guard and handler. */
const CategoryStep: React.FC<{step: Step}> = observer(({step}) => {
    const categoryId = step.categoryId ?? -1;
    const options = getCategoryOptions(s.prod.categories, categoryId);
    const picked = options.find(o => o.id === s.ps.category_map_id);
    return (
        <StepFrame
            step={step}
            title={options[0]?.CategoryName ?? ''}
            picked={picked?.Name}
        >
            <div className="pp3-category">
                <Category CategoryID={categoryId} />
            </div>
        </StepFrame>
    );
});

const FrameStep: React.FC<{step: Step}> = observer(({step}) => {
    const order = mouldingOrder(s.prod.mouldings);
    const all = order.sortedMouldings;
    const shown = redesign.framesOpen ? all : all.slice(0, FRAMES_SHOWN);
    const cdn = Portal?.CDNDomain;
    return (
        <StepFrame
            step={step}
            title="Choose your frame"
            picked={s.ui.title_moulding}
        >
            <div
                className="pp3-tiles pp3-frames"
                role="group"
                aria-label="Frames"
            >
                {shown.map(m => {
                    const selected = s.ps.moulding_id === m.id;
                    const fast = fastShipItem() && isFastShipMoulding(m);
                    const trending = isTrendingMoulding(m, order);
                    return (
                        <div
                            key={m.id}
                            className="pp3-tile-wrap"
                        >
                            <button
                                type="button"
                                className={`pp3-tile${selected ? ' selected' : ''}`}
                                aria-pressed={selected}
                                aria-label={`${m.title}${fast ? ', Fast Ship' : ''}${trending ? ', trending' : ''}`}
                                title={m.title}
                                onClick={() => selectMoulding(m.id)}
                            >
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet={webp(`https://${cdn}/images/entities/moulding/${m.name.toLowerCase()}_thumb_90.png`)}
                                    />
                                    <img
                                        className="pp3-tile-img"
                                        src={`https://${cdn}/images/entities/moulding/${m.name.toLowerCase()}_thumb_90.png`}
                                        alt=""
                                        loading="lazy"
                                    />
                                </picture>
                                {m.IsRecycled && (
                                    <RecycledIcon
                                        width={16}
                                        top={'4px'}
                                        left={'4px'}
                                    />
                                )}
                            </button>
                            {fast ? <FastShipPill /> : trending ? <span className="pp3-pill pp3-pill-trend">Trending</span> : null}
                        </div>
                    );
                })}
                {all.length > FRAMES_SHOWN && (
                    <button
                        type="button"
                        className="pp3-more"
                        aria-expanded={redesign.framesOpen}
                        onClick={() => redesign.toggle('framesOpen')}
                    >
                        {redesign.framesOpen ? 'See fewer frames' : `See all ${all.length} frames`}
                    </button>
                )}
            </div>
        </StepFrame>
    );
});

const MatSealStep: React.FC<{step: Step}> = observer(({step}) => {
    const cdn = Portal?.CDNDomain;
    const mats = matSectionShown();
    const seals = insigniaSectionShown();
    const matList = mats ? matTiles(redesign.matsOpen ? 1000 : MATS_SHOWN) : [];
    const sealAll = seals ? visibleInsignias() : [];
    const sealList = redesign.sealsOpen ? sealAll : sealAll.slice(0, SEALS_SHOWN);
    const noSeal = seals && s.prod.InsigniaID < 1;
    const sealPicked = s.ps.insignia_id > 0 ? getTitle() : noSeal ? 'No seal' : '';
    const title = mats && seals ? 'Choose your mat & seal' : mats ? 'Choose your mat' : 'Choose your seal';
    const picked = [mats ? s.ui.title_mat_combo : '', seals ? sealPicked : ''].filter(Boolean).join(', ');
    const tileCount = matList.length + (mats && s.prod.mats.length > MATS_SHOWN ? 1 : 0) + sealList.length + (noSeal ? 1 : 0) + (sealAll.length > SEALS_SHOWN ? 1 : 0);
    // the seal's Top/Bottom select (types 87 and 91) sits under the "Seal" label, which the shared row hides
    const position = seals && insigniaPositionShown();
    // mats and seals share a row only where it holds them: 6 captioned tiles in the steps column, 3 on a 360px phone
    const compact = mats && seals && !position && tileCount <= (isRedesignPhone(screenResizeStore.width) ? 3 : 6);
    const matBg = `#${s.ui.color_mat1 || 'FFFFFF'}`;

    return (
        <StepFrame
            step={step}
            title={title}
            picked={picked}
        >
            <div className={`pp3-matseal${compact ? ' compact' : ''}`}>
                {mats && (
                    <div className="pp3-tilegroup">
                        <span className="pp3-tilegroup-label">Mat</span>
                        <div
                            className="pp3-tiles"
                            role="group"
                            aria-label="Mats"
                        >
                            {matList.map(m => {
                                const selected = s.ps.matcombo_id === m.id;
                                const fast = fastShipItem() && isFastShipMat(m);
                                const src = `https://${cdn}/images/entities/matcombos/90_${m.mat1_id}${m.mat2_id && m.mat2_id > 0 ? `_${m.mat2_id.toString()}` : ''}.png`;
                                return (
                                    <div
                                        key={m.id}
                                        className="pp3-tile-wrap captioned"
                                    >
                                        <button
                                            type="button"
                                            className={`pp3-tile${selected ? ' selected' : ''}`}
                                            aria-pressed={selected}
                                            aria-label={`Mat: ${m.title}${fast ? ', Fast Ship' : ''}`}
                                            onClick={() => selectMat(m.id)}
                                        >
                                            <picture>
                                                <source
                                                    type="image/webp"
                                                    srcSet={src.replace('.png', '.webp')}
                                                />
                                                <img
                                                    className="pp3-tile-img"
                                                    src={src}
                                                    alt=""
                                                    loading="lazy"
                                                />
                                            </picture>
                                        </button>
                                        {fast && <FastShipPill />}
                                        <span
                                            className="pp3-caption"
                                            aria-hidden="true"
                                        >
                                            {m.title}
                                        </span>
                                    </div>
                                );
                            })}
                            {s.prod.mats.length > MATS_SHOWN && (
                                <div className="pp3-tile-wrap captioned">
                                    <button
                                        type="button"
                                        className="pp3-more"
                                        aria-expanded={redesign.matsOpen}
                                        onClick={() => redesign.toggle('matsOpen')}
                                    >
                                        {redesign.matsOpen ? 'See fewer mats' : `See ${s.prod.mats.length - MATS_SHOWN} more mat${s.prod.mats.length - MATS_SHOWN === 1 ? '' : 's'}`}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {mats && seals && (
                    <span
                        className="pp3-divider"
                        aria-hidden="true"
                    />
                )}
                {seals && (
                    <div className="pp3-tilegroup">
                        <span className="pp3-tilegroup-label">Seal</span>
                        {position && (
                            <div className="pp3-position">
                                <InsigniaPosition />
                            </div>
                        )}
                        <div
                            className="pp3-tiles"
                            role="group"
                            aria-label="Seals"
                        >
                            {noSeal && (
                                <div className="pp3-tile-wrap captioned">
                                    <button
                                        type="button"
                                        className={`pp3-tile pp3-tile-none${s.ps.insignia_id > 0 ? '' : ' selected'}`}
                                        aria-pressed={!(s.ps.insignia_id > 0)}
                                        aria-label="No seal"
                                        onClick={() => removeInsignia()}
                                        style={{backgroundColor: matBg}}
                                    />
                                    <span
                                        className="pp3-caption"
                                        aria-hidden="true"
                                    >
                                        No seal
                                    </span>
                                </div>
                            )}
                            {sealList.map(m => {
                                const selected = s.ps.insignia_id > 0 && s.preferredInsigniaId(s.ps.insignia_id) === m.id;
                                const name = getTitle(m.title, m.ins_color);
                                return (
                                    <div
                                        key={m.id}
                                        className="pp3-tile-wrap captioned"
                                    >
                                        <button
                                            type="button"
                                            className={`pp3-tile pp3-tile-seal${selected ? ' selected' : ''}`}
                                            aria-pressed={selected}
                                            aria-label={`Seal: ${name}`}
                                            title={name}
                                            onClick={() => selectInsignia(m.id)}
                                            style={{backgroundColor: matBg}}
                                        >
                                            <img
                                                className="pp3-tile-img"
                                                src={`https://${cdn}/images/entities/insignia/v2/${m.name.toLowerCase()}_90.png`}
                                                alt=""
                                                loading="lazy"
                                            />
                                        </button>
                                        <span
                                            className="pp3-caption"
                                            aria-hidden="true"
                                        >
                                            {name}
                                        </span>
                                    </div>
                                );
                            })}
                            {sealAll.length > SEALS_SHOWN && (
                                <div className="pp3-tile-wrap captioned">
                                    <button
                                        type="button"
                                        className="pp3-more"
                                        aria-expanded={redesign.sealsOpen}
                                        onClick={() => redesign.toggle('sealsOpen')}
                                    >
                                        {redesign.sealsOpen ? 'See fewer seals' : `See ${sealAll.length - SEALS_SHOWN} more seal${sealAll.length - SEALS_SHOWN === 1 ? '' : 's'}`}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </StepFrame>
    );
});

/** "Included" for no charge, else "+$35". */
const glassPrice = (price: string): string => (!price || price === '$0' ? 'Included' : `+${price}`);

const GlassStep: React.FC<{step: Step}> = observer(({step}) => {
    const offered = s.glass.filter(isOfferedGlassTile);
    const current = offered.find(g => g.id === s.ps.glass_id) ?? s.glass.find(g => g.id === s.ps.glass_id);
    const acrylic = isAcrylicGlazing();
    return (
        <StepFrame
            step={step}
            title="Choose your glass"
            picked={current ? `${current.title}${acrylic ? ' acrylic' : ''}` : ''}
        >
            <div
                className="pp3-glass"
                role="radiogroup"
                aria-label={acrylic ? 'Glazing' : 'Glass'}
            >
                {offered.map(g => {
                    const selected = s.ps.glass_id === g.id;
                    return (
                        <label
                            key={g.id}
                            className={`pp3-glass-option${selected ? ' selected' : ''}`}
                            data-glass-id={g.id}
                        >
                            <input
                                type="radio"
                                name="pp3-glass"
                                value={g.id}
                                checked={selected}
                                onChange={() => selectGlass(g.id)}
                            />
                            <span className="pp3-glass-body">
                                <span className="pp3-glass-row">
                                    <span className="pp3-glass-name">{g.title}</span>
                                    {g.id === 1 && fastShipItem() && <FastShipPill />}
                                    {g.id === GLASS_TAG_ID && <span className="pp3-pill pp3-pill-tag">{GLASS_TAG}</span>}
                                    <span className="pp3-glass-price">{glassPrice(g.price)}</span>
                                </span>
                                <span className="pp3-glass-benefit">{GLASS_BENEFITS[g.id]}</span>
                            </span>
                        </label>
                    );
                })}
            </div>
            <p className="pp3-glass-info">
                {acrylic && (
                    <>
                        <button
                            type="button"
                            className="pp3-link"
                            onClick={() => modal('modal-plexi-info')}
                            aria-haspopup="dialog"
                            aria-controls="modal-plexi-info"
                        >
                            Why does this frame have acrylic?
                        </button>{' '}
                    </>
                )}
                <button
                    type="button"
                    className="pp3-link"
                    onClick={() => modal('modal-glass')}
                    aria-haspopup="dialog"
                    aria-controls="modal-glass"
                >
                    Learn more
                </button>{' '}
                about our document protection options.
            </p>
        </StepFrame>
    );
});

/** "Your frame: ..." after the last step on phones. */
/** The steps column: the heading, the numbered steps in their groups, then the Add To Cart row, the ship line and the trust line. */
const Stepper: React.FC<{plan: StepPlan; summary: string}> = observer(({plan, summary}) => {
    const intro = stepsIntro(plan);
    const degreeStep = plan.steps.find(step => step.id === 'degree');
    const firstYours = plan.steps.find(step => !step.fit);

    const renderStep = (step: Step) => {
        switch (step.id) {
            case 'school':
                return (
                    <SchoolStep
                        key={step.id}
                        step={step}
                        plan={plan}
                    />
                );
            case 'degree':
                return (
                    <DegreeStep
                        key={step.id}
                        step={step}
                    />
                );
            case 'year':
                return (
                    <YearStep
                        key={step.id}
                        step={step}
                        degreeStep={degreeStep}
                    />
                );
            case 'frame':
                return (
                    <FrameStep
                        key={step.id}
                        step={step}
                    />
                );
            case 'mat':
                return (
                    <MatSealStep
                        key={step.id}
                        step={step}
                    />
                );
            case 'glass':
                return (
                    <GlassStep
                        key={step.id}
                        step={step}
                    />
                );
            default:
                return (
                    <CategoryStep
                        key={step.id}
                        step={step}
                    />
                );
        }
    };

    return (
        <>
            <h2
                id="pp3-steps-title"
                className="pp3-steps-title"
            >
                {plan.sizeQuestions ? 'Build your frame' : `Make it yours in ${plan.steps.length} step${plan.steps.length === 1 ? '' : 's'}`}
            </h2>
            <p className="pp3-steps-intro">
                <span className="pp3-wide">{intro.wide}</span>
                <span className="pp3-narrow">{intro.narrow}</span>
            </p>
            {plan.sizeQuestions && <p className="pp3-group">Make sure it fits</p>}
            <div className="pp3-stepper">
                {plan.steps.map(step => (
                    <React.Fragment key={step.id}>
                        {plan.sizeQuestions && step === firstYours && (
                            <p className="pp3-group pp3-group-yours">
                                Make it yours <span className="pp3-wide">(already set to the frame in the photo)</span>
                                <span className="pp3-narrow">(set to the photo)</span>
                            </p>
                        )}
                        {renderStep(step)}
                    </React.Fragment>
                ))}
                <AtcRow summary={summary} />
            </div>
        </>
    );
});

export default Stepper;
